'use client';

import type { CSSProperties } from 'react';
import Link from 'next/link';
import { IconArrowRight, IconSparkles } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { formatHourlyRate, type GPUProduct } from '@/lib/data/gpu-products';
import { GpuCharacter, HUES, personaFor } from '@/components/shop/homepage/gpu-characters';
import { CYAN, useCaseHue } from './arcade-classes';
import { GpuChip, Platform, RisingPixels, type RisingPixel } from './arcade-art';

const PIXELS: RisingPixel[] = [
  [10, 6, 0.6, 4.4, 'var(--primary)'],
  [24, 5, 2.4, 5.6, 'var(--accent)'],
  [38, 7, 1.2, 4.8, 'var(--primary)'],
  [62, 5, 3.4, 5.2, CYAN],
  [76, 8, 0.2, 6, 'var(--primary)'],
  [90, 5, 1.9, 4.6, 'var(--chart-4)'],
];

interface GPULegendaryDropProps {
  gpu: GPUProduct;
  index: number;
  /** Create-form link for this GPU, built with `machineCreateHref()`. */
  href: string;
  price: number;
  priceLabel: string;
}

/**
 * The featured GPU, revealed like a legendary item: a wide banner fading from
 * the accent into the primary, with the character on a lit pedestal under
 * rotating light rays.
 */
export function GPULegendaryDrop({ gpu, index, href, price, priceLabel }: GPULegendaryDropProps) {
  const persona = personaFor(gpu.id, index);
  const hue = HUES[persona.hue].cssVar;
  const model = gpu.name.replace(/^NVIDIA\s+/, '');

  return (
    <article
      aria-labelledby="legendary-title"
      style={{ '--hue': hue } as CSSProperties}
      className="ga-legend relative isolate overflow-hidden rounded-[1.75rem]"
    >
      <div aria-hidden className="scanlines absolute inset-0 -z-10 opacity-50" />
      <div className="grid grid-cols-1 items-center lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* Art */}
        <div aria-hidden className="relative h-72 sm:h-80 lg:h-[400px]">
          <div className="ga-legend-rays absolute left-1/2 top-1/2 size-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full" />
          <RisingPixels pixels={PIXELS} />
          <div className="absolute bottom-6 left-1/2 w-[48%] max-w-[260px] -translate-x-1/2">
            <div className="bob-a relative z-10">
              <div className="select-aura absolute -inset-[24%] -z-10 rounded-full blur-2xl" />
              <GpuCharacter
                persona={persona}
                uid="arcade-legendary"
                className="drop-shadow-[0_24px_30px_rgba(0,0,0,0.5)]"
              />
            </div>
            <Platform hue={hue} className="-mt-[6%] h-10 w-full sm:h-12" />
          </div>
          <div className="bob-b absolute left-[12%] top-[16%] w-10 rotate-[-10deg]">
            <GpuChip hue="var(--primary)" />
          </div>
          <div className="bob-c absolute right-[14%] top-[26%] w-8 rotate-[12deg]">
            <GpuChip hue="var(--accent)" />
          </div>
          <span className="absolute left-5 top-5 font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/80">
            {persona.codename} · {persona.role}
          </span>
        </div>

        {/* Details */}
        <div className="relative px-5 pb-7 pt-2 sm:px-8 lg:py-10 lg:pl-4 lg:pr-12">
          <div className="flex flex-wrap items-center gap-2">
            <span className="ga-legend-label inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-heading text-xs font-bold uppercase tracking-[0.16em]">
              <IconSparkles className="size-3.5" />
              Featured loadout
            </span>
            {gpu.badge && (
              <span className="rounded-md border border-foreground/25 bg-background/40 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-foreground">
                {gpu.badge} pick
              </span>
            )}
          </div>

          <p className="mt-4 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-foreground/70">
            Legendary drop · NVIDIA
          </p>
          <h2
            id="legendary-title"
            className="font-heading text-5xl font-black uppercase leading-[0.9] tracking-tight text-foreground sm:text-6xl lg:text-7xl"
          >
            {model}
          </h2>
          <p className="mt-3 max-w-lg text-base text-foreground/80">
            {gpu.consumerEquivalent}. {persona.tagline}
          </p>

          <dl className="mt-6 grid max-w-lg grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: 'VRAM', value: gpu.vram, unit: 'GB' },
              { label: 'vCPUs', value: gpu.vcpus, unit: '' },
              { label: 'RAM', value: gpu.ram, unit: 'GB' },
            ].map((spec) => (
              <div
                key={spec.label}
                className="rounded-xl border border-foreground/15 bg-background/45 px-3 py-3 backdrop-blur-sm"
              >
                <dt className="font-mono text-[10px] font-semibold uppercase tracking-wider text-foreground/70">
                  {spec.label}
                </dt>
                <dd className="mt-1 font-heading text-3xl font-bold tabular-nums leading-none text-foreground">
                  {spec.value}
                  {spec.unit && <span className="ml-1 text-sm font-semibold text-foreground/70">{spec.unit}</span>}
                </dd>
              </div>
            ))}
          </dl>

          <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Recommended for">
            {gpu.useCases.map((useCase) => (
              <li
                key={useCase}
                style={{ '--hue': useCaseHue(useCase) } as CSSProperties}
                className="ga-chip-tag rounded-md bg-background/40 px-2 py-1 text-xs font-medium text-foreground"
              >
                {useCase}
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-wrap items-end gap-x-8 gap-y-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-foreground/70">{priceLabel}</p>
              <p className="font-heading text-4xl font-black tabular-nums leading-none text-foreground">
                {formatHourlyRate(price)}
              </p>
            </div>
            <Button asChild size="lg" className="arcade-press h-14 rounded-xl px-8 text-lg font-bold">
              <Link href={href}>
                Configure now
                <IconArrowRight className="size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}
